/**
 * Icon helpers and SVG lookup tables for {@link SidebarRail}.
 *
 * Extracted verbatim from SidebarRail.tsx (zero behavior change).
 */

import { ICON_BY_KEY, ARCHETYPE_LABEL_ICONS } from '@/config/module-icons';

// Re-exported so existing/external consumers keep importing the icon config from here.
export { ICON_BY_KEY, ARCHETYPE_LABEL_ICONS } from '@/config/module-icons';

/**
 * Temporary icon helper for the new primary rail items experiment.
 * TODO: Replace with a proper shared icon system once the Primary Rail Modules feature lands.
 * Should live in a shared place (e.g. next to MODULE_CATALOG in types/modules.ts).
 */
export function getSimpleIconForType(type: string): React.ReactNode {
  const common = { width: 20, height: 20, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

  switch (type) {
    case 'task_list':
      return <svg {...common}><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>;
    case 'event_list':
      return <svg {...common}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>;
    case 'contact_list':
      return <svg {...common}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    case 'note_grid':
    case 'drive':
      return <svg {...common}><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></svg>;
    case 'report':
    case 'pages':
      return <svg {...common}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>;
    case 'gmail':
      return <svg {...common}><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,6 12,13 2,6" /></svg>;
    case 'agents':
      return <svg {...common}><rect x="4" y="8" width="16" height="11" rx="2" /><path d="M12 4v4" /><circle cx="12" cy="3" r="1" /><circle cx="9" cy="13" r="1" /><circle cx="15" cy="13" r="1" /></svg>;
    default:
      return <svg {...common}><rect x="3" y="3" width="18" height="18" rx="2" /></svg>;
  }
}

export function getPrimaryModuleIcon(type: string, label: string, config?: Record<string, unknown>): React.ReactNode {
  const configIcon = typeof config?.icon === 'string' ? ICON_BY_KEY[config.icon] : undefined;
  return configIcon ?? ARCHETYPE_LABEL_ICONS[label] ?? getSimpleIconForType(type);
}

/** SVG icons + labels for known plugin module types. */
export const PLUGIN_MODULE_META: Record<string, { label: string; icon: React.ReactNode }> = {
  news: { label: 'News', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
      <path d="M18 14h-8" /><path d="M15 18h-5" /><rect x="10" y="6" width="8" height="5" rx="1" />
    </svg>
  )},
  'shopify-crm': { label: 'Shopify', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  )},
  mixpanel: { label: 'Mixpanel', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  )},
  'app-analytics': { label: 'Analytics', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  )},
  canvas: { label: 'Dashboard', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" />
      <rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" />
    </svg>
  )},
};
